import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ContrastMode = 'normal' | 'high' | 'system';

type DisplaySettings = {
  themeMode: ThemeMode;
  contrastMode: ContrastMode;
  redactNames: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  setContrastMode: (mode: ContrastMode) => void;
  setRedactNames: (on: boolean) => void;
  // Resolved values after applying system preferences
  resolvedTheme: 'light' | 'dark';
  resolvedContrast: 'normal' | 'high';
};

const DisplaySettingsContext = createContext<DisplaySettings | undefined>(undefined);

const THEME_STORAGE_KEY = 'simscope:themeMode';
const CONTRAST_STORAGE_KEY = 'simscope:contrastMode';
const REDACT_STORAGE_KEY = 'simscope:redactNames';

const readStoredValue = <T extends string>(key: string, validValues: T[], fallback: T): T => {
  try {
    const stored = localStorage.getItem(key);
    if (stored && validValues.includes(stored as T)) {
      return stored as T;
    }
  } catch {
    // localStorage unavailable (SSR, private mode, etc.)
  }
  return fallback;
};

export function DisplaySettingsProvider({ children }: { children: React.ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() =>
    readStoredValue(THEME_STORAGE_KEY, ['light', 'dark', 'system'], 'system')
  );
  const [contrastMode, setContrastModeState] = useState<ContrastMode>(() =>
    readStoredValue(CONTRAST_STORAGE_KEY, ['normal', 'high', 'system'], 'system')
  );
  const [redactNames, setRedactNamesState] = useState<boolean>(
    () => readStoredValue(REDACT_STORAGE_KEY, ['on', 'off'], 'off') === 'on'
  );

  // Track OS-level preferences for system mode
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [systemPrefersHighContrast, setSystemPrefersHighContrast] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-contrast: more)').matches
  );

  useEffect(() => {
    const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const contrastQuery = window.matchMedia('(prefers-contrast: more)');

    const handleDarkChange = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    const handleContrastChange = (e: MediaQueryListEvent) =>
      setSystemPrefersHighContrast(e.matches);

    darkQuery.addEventListener('change', handleDarkChange);
    contrastQuery.addEventListener('change', handleContrastChange);

    return () => {
      darkQuery.removeEventListener('change', handleDarkChange);
      contrastQuery.removeEventListener('change', handleContrastChange);
    };
  }, []);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  };

  const setContrastMode = (mode: ContrastMode) => {
    setContrastModeState(mode);
    try {
      localStorage.setItem(CONTRAST_STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  };

  const setRedactNames = (on: boolean) => {
    setRedactNamesState(on);
    try {
      localStorage.setItem(REDACT_STORAGE_KEY, on ? 'on' : 'off');
    } catch {
      // ignore
    }
  };

  const resolvedTheme: 'light' | 'dark' =
    themeMode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : themeMode;

  const resolvedContrast: 'normal' | 'high' =
    contrastMode === 'system' ? (systemPrefersHighContrast ? 'high' : 'normal') : contrastMode;

  // Apply attributes to <html> so custom CSS can respond
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolvedTheme);
    root.setAttribute('data-contrast', resolvedContrast);
    // Hint to the browser for native controls (scrollbars, form controls)
    root.style.colorScheme = resolvedTheme;
  }, [resolvedTheme, resolvedContrast]);

  const value = useMemo(
    () => ({
      themeMode,
      contrastMode,
      redactNames,
      setThemeMode,
      setContrastMode,
      setRedactNames,
      resolvedTheme,
      resolvedContrast,
    }),
    [themeMode, contrastMode, redactNames, resolvedTheme, resolvedContrast]
  );

  return (
    <DisplaySettingsContext.Provider value={value}>{children}</DisplaySettingsContext.Provider>
  );
}

export function useDisplaySettings(): DisplaySettings {
  const ctx = useContext(DisplaySettingsContext);
  if (!ctx) {
    throw new Error('useDisplaySettings must be used within DisplaySettingsProvider');
  }
  return ctx;
}
